import { useState } from "react";
import { Button, More, Pill } from "@omnabase/ui";
import type { Project, SendingDomain } from "@omnabase/shared";
import { useSession } from "../../shell/store";

/** The own domain's DNS records (M7 round seven) with the first setup round's help (item 6, 2026-10-22): where the records go (the name
 *  servers' provider, looked up when the domain was added, or Cloudflare as the example), Cloudflare's "DNS only", Amazon's hour; and
 *  "Check now" busy while Amazon is asked (item 7). Every line under 30 words. Since 2026-10-28 the records fold under "DNS records" once
 *  they are not the next step (verified, or paused for the plan) and stay open while pending, failed or paused for DKIM; "Delete domain"
 *  shows only on the project that added the domain, and its confirmation names the other projects that send from it. */
const STATE_LABEL: Record<SendingDomain["status"], string> = { pending: "Pending", verified: "Verified", failed: "Failed", paused: "Paused" };
const AMAZON_LINE = "Amazon usually sees them within an hour; we look again every hour for seven days.";

/** the help above the records while they are still needed: where they go, Cloudflare's trap, Amazon's hour */
export function dnsHelpLines(provider: string | null): string[] {
  if (!provider) return ["Add the three CNAME records where your domain's name servers point, for example Cloudflare if you use it.", "On Cloudflare set them to DNS only, not proxied.", AMAZON_LINE];
  return [`Your name servers: ${provider}.`, `Add the three CNAME records at ${provider}.`, ...(provider === "Cloudflare" ? ["On Cloudflare set them to DNS only, not proxied."] : []), AMAZON_LINE];
}
/** the line under the state once the records are not the next step: what it means and what to do */
export function stateLine(row: SendingDomain, from: string): string {
  if (row.status === "verified") return `Replies from this project leave from ${from}. Customers' answers still come back to the ticket.`;
  if (row.status === "failed") return "Amazon did not find the records. Check them at your DNS host, then press Check now.";
  return row.pausedReason === "plan" ? "Paused: your plan does not include own domains. Replies leave from our domain meanwhile; a check on a plan with own domains switches it back on." : "Paused: the DKIM records stopped working. Replies leave from our domain meanwhile; put the records back and press Check now.";
}

type Props = { row: SendingDomain; project: Project; canCheck: boolean; canEdit: boolean; /** the project added it (or none is recorded) */ canDelete: boolean; /** the other projects sending from it */ others: string[]; checking: boolean; onCheck: () => void; onDelete: () => void };
export function DnsRecords({ row, project: p, canCheck, canEdit, canDelete, others, checking, onCheck, onDelete }: Props) {
  const help = row.status === "pending" || row.status === "failed" ? dnsHelpLines(row.nameServers?.provider ?? null) : [];
  const folded = row.status === "verified" || (row.status === "paused" && row.pausedReason === "plan");
  return (
    <section className="ob-dns" role="region" aria-label="DNS records">
      <div className="ob-dns__head"><strong className="ob-dns__domain">{row.domain}</strong><Pill>{STATE_LABEL[row.status]}</Pill></div>
      {row.status !== "pending" && <p className="ob-dns__line">{stateLine(row, `${p.sendingLocal}@${row.domain}`)}</p>}
      {help.map((l) => <p key={l} className="ob-dns__line">{l}</p>)}
      {folded ? <More label="DNS records"><RecordList row={row} /></More> : <RecordList row={row} />}
      {canEdit && <DnsActions row={row} canCheck={canCheck} canDelete={canDelete} others={others} checking={checking} onCheck={onCheck} onDelete={onDelete} />}
    </section>
  );
}

/** the records to copy, each name and value with its copy button */
function RecordList({ row }: { row: SendingDomain }) {
  const { toast } = useSession();
  const copy = (text: string, said: string) => { void navigator.clipboard?.writeText(text); toast(said, { kind: "success" }); };
  return (
    <ul className="ob-dns__records">
      {row.records.map((r, i) => (
        <li key={r.name} className="ob-dns__record">
          <span className="ob-dns__type">{r.type}{r.optional && <span className="ob-dns__optional">Optional</span>}</span>
          <span className="ob-dns__field"><span className="ob-dns__label">Name</span><code className="ob-dns__value">{r.name}</code><Button size="sm" aria-label={`Copy name of record ${i + 1}`} onClick={() => copy(r.name, "Name copied")}>Copy</Button></span>
          <span className="ob-dns__field"><span className="ob-dns__label">{r.type === "CNAME" ? "Points to" : "Value"}</span><code className="ob-dns__value">{r.value}</code><Button size="sm" aria-label={`Copy value of record ${i + 1}`} onClick={() => copy(r.value, "Value copied")}>Copy</Button></span>
          {r.optional && <span className="ob-dns__note">A suggestion for a domain without a DMARC policy; keep yours if you have one.</span>}
        </li>
      ))}
    </ul>
  );
}

/** the confirmation's words: who goes back to our domain */
export const deleteQuestion = (domain: string, others: string[]) => others.length ? `Delete ${domain}? Replies from this project and from ${others.join(", ")} go back to our domain.` : `Delete ${domain}? Replies go back to our domain.`;

function DnsActions({ row, canCheck, canDelete, others, checking, onCheck, onDelete }: Omit<Props, "canEdit" | "project">) {
  const [confirming, setConfirming] = useState(false);
  const verified = row.status === "verified";
  return (
    <div className="ob-dns__actions">
      {canCheck && <Button variant={verified ? "secondary" : "primary"} size="sm" busy={checking} onClick={onCheck}>Check now</Button>}
      {canDelete && (
        <span className="ob-dns__actions-end">
          {confirming
            ? <><span className="ob-dns__confirm">{deleteQuestion(row.domain, others)}</span><Button size="sm" onClick={() => setConfirming(false)}>Keep</Button><Button size="sm" onClick={onDelete}>Delete</Button></>
            : <Button size="sm" onClick={() => setConfirming(true)}>Delete domain</Button>}
        </span>
      )}
    </div>
  );
}
